import RadioButtonOutline from '@/components/RadioButtonOutline';
import SelectButtonOutline from '@/components/SelectButtonOutline';
import { LanguageType } from '@/models/LanguageType';
import Option from '@/models/Option';
import { StudentLocale } from '@/models/StudentLocale';
import { setLocale as setLocalSvc } from '@/services/IdentityService';
import { ApplicationState } from '@/store';
import { setContentLocaleRequest, setLocale, setLocaleRequest } from '@/store/locale/action';
import en from '@/translations/en';
import pt from '@/translations/pt';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import _ from 'underscore';
import './style.scss';

interface Props {
    handleModalClose?: () => void;
}

const ChangeLanguage = ({ handleModalClose }: Props) => {
    const { strings, availableContentLanguages, person, isAdmin } = useSelector((state: ApplicationState) => {
        return {
            strings: state.locale.data,
            availableContentLanguages: state.locale.availableContentLanguages,
            person: state.identity.data.person,
            isAdmin: state.identity.data.person.dynamicAttributes && state.identity.data.person.dynamicAttributes.selectedProfile === 'HR',
        };
    });

    const initialLanguage: LanguageType = strings._LANGUAGE;
    const initialContentLanguages: LanguageType[] = _.isEmpty(availableContentLanguages) ? [strings._LANGUAGE] : availableContentLanguages;

    const [selectedLanguage, setSelectedLanguage] = useState<LanguageType>(initialLanguage);
    const [selectedContentLanguages, setSelectedContentLanguages] = useState<LanguageType[]>(initialContentLanguages);

    const dispatch: Dispatch = useDispatch();

    const handleSubmit = () => {
        if (!_.isEqual(selectedLanguage, initialLanguage)) {
            dispatch(setLocale(selectedLanguage === 'en' ? en : pt));
            setLocalSvc(selectedLanguage);
            person.locale = selectedLanguage;
            dispatch(setLocaleRequest(person));
        }

        if (!_.isEqual(selectedContentLanguages, initialContentLanguages)) {
            const studentLocaleList: StudentLocale[] = selectedContentLanguages.map((lang) => ({ personId: person.id, locale: lang }));
            dispatch(setContentLocaleRequest(studentLocaleList));
        }

        closeModal();
    };

    const closeModal = () => {
        const htmlElement: HTMLElement | null = document.getElementById('change-modal');
        htmlElement && htmlElement.click();
        handleModalClose && handleModalClose();
    };

    const options = useMemo(() => {
        return [
            {
                key: 'pt',
                value: strings.LANGUAGE_PT,
            },
            {
                key: 'en',
                value: strings.LANGUAGE_EN,
            },
        ];
    }, [strings.LANGUAGE_EN, strings.LANGUAGE_PT]);

    const changeSelectedOptionLang = useCallback((option: Option) => {
        setSelectedLanguage(option.key as LanguageType);
    }, []);

    const changeSelectedContentOptionLang = useCallback((selectedOptions: string[]) => {
        setSelectedContentLanguages(selectedOptions as LanguageType[]);
    }, []);

    const disableActionButton = useCallback(() => {
        if (_.isEmpty(selectedContentLanguages)) {
            return true;
        }

        return initialLanguage === selectedLanguage && _.isEqual(initialContentLanguages, selectedContentLanguages);
    }, [selectedLanguage, selectedContentLanguages]);

    return (
        <div className="modal-change-language">
            <div className="language-info-container">
                <h2>{strings.SYSTEM_LANGUAGE}</h2>
                <div className="divider" />
                <span>{strings.SYSTEM_LANGUAGE_INFO}</span>
            </div>

            <div className="system-language-options">
                <RadioButtonOutline onSelectOption={changeSelectedOptionLang} options={options} selectedOptionKey={selectedLanguage} />
            </div>

            <div className="language-info-container">
                <h2>{strings.CONTENT_LANGUAGE}</h2>
                <div className="divider" />
                <span dangerouslySetInnerHTML={{ __html: strings.CONTENT_LANGUAGE_INFO }} />
            </div>

            <div className="system-language-options">
                <SelectButtonOutline
                    onSelectOption={changeSelectedContentOptionLang}
                    options={options}
                    selectedOptionsKey={selectedContentLanguages}
                />
            </div>

            {isAdmin && (
                <div className="change-language-title">
                    <span dangerouslySetInnerHTML={{ __html: strings.CHANGE_LANGUAGE_TEXT }} />
                </div>
            )}
            <div className="change-language-footer">
                <button
                    className="btn btn-secondary change-button"
                    data-dismiss="modal"
                    disabled={disableActionButton()}
                    onClick={handleSubmit}
                    type="button"
                >
                    {strings.CHANGE}
                </button>
            </div>
        </div>
    );
};

export default ChangeLanguage;
